import { useCallback } from "react";
import { usePreview } from "../contexts/PreviewContext";

export interface PreviewCommit {
  hash: string;
  message: string;
  timestamp: string;
  parent: string;
  hasTree: boolean;
  fileCount?: number;
  treeHash?: string;
}

export interface PreviewLogResult {
  branch: string;
  currentBranch: string;
  commits: PreviewCommit[];
  ahead: number;
  behind: number;
}

export interface PreviewStatusResult {
  branch: string;
  isDirty: boolean;
  ahead: number;
  behind: number;
  headCommit: string;
  tracksRemote?: string;
  remoteBranchId?: string;
  changes: {
    slugPath: string;
    changeType: string;
    nodeType: string;
    details: string;
  }[];
}

export interface CompareResult {
  added: Array<{ slugPath: string; title: string; status: string; newBody: string }>;
  removed: Array<{ slugPath: string; title: string; status: string; oldBody: string }>;
  modified: Array<{
    slugPath: string;
    title: string;
    status: string;
    oldBody: string;
    newBody: string;
    oldTitle: string;
    newTitle: string;
  }>;
  totalChanges: number;
}

export interface RemoteBranch {
  name: string;
  id: string;
  isTracked: boolean;
}

export interface ObjectStoreWriteResult {
  node: Record<string, unknown>;
  pendingTreeHash: string;
}

export interface ObjectStoreCommitResult {
  commitHash: string;
  newHead: string;
  sequence: number;
}

export interface AutoFetchStatusResult {
  enabled: boolean;
  interval: number;
  lastFetch: string;
  newDataAvailable: boolean;
}

export class ReadOnlyError extends Error {
  readonly readOnly = true;
  constructor(message: string) {
    super(message);
    this.name = "ReadOnlyError";
  }
}

// NeedsRebaseError is thrown by push/pull when the local branch has diverged
// from remote and cannot auto-merge. Callers should open the
// RebaseSessionDialog using the included branch/branchId.
export class NeedsRebaseError extends Error {
  readonly needsRebase = true;
  readonly branch: string;
  readonly branchId: string;
  readonly ahead?: number;
  readonly behind?: number;
  constructor(message: string, branch: string, branchId: string, ahead?: number, behind?: number) {
    super(message);
    this.name = "NeedsRebaseError";
    this.branch = branch;
    this.branchId = branchId;
    this.ahead = ahead;
    this.behind = behind;
  }
}

async function previewFetch(url: string, opts?: RequestInit) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    if (res.status === 403 && data.readOnly) {
      throw new ReadOnlyError(data.error || "Target reference is read-only");
    }
    if (res.status === 409 && data.needsRebase) {
      throw new NeedsRebaseError(
        data.error || "Branch has diverged — rebase required",
        data.branch || "",
        data.branchId || "",
        data.ahead,
        data.behind,
      );
    }
    throw new Error(data.error || data.message || `Request failed: ${res.statusText}`);
  }
  return res.json();
}

export function usePreviewApi() {
  const { isLocalPreview } = usePreview();

  const commit = useCallback(async (message: string) => {
    return previewFetch("/api/preview/commit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
    });
  }, []);

  const selectiveCommit = useCallback(async (message: string, paths: string[]) => {
    return previewFetch("/api/preview/commit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, paths }),
    });
  }, []);

  const getLog = useCallback(async (branch?: string, limit?: number, source?: string): Promise<PreviewLogResult> => {
    const params = new URLSearchParams();
    if (branch) params.set("branch", branch);
    if (limit) params.set("limit", String(limit));
    if (source) params.set("source", source);
    return previewFetch(`/api/preview/log?${params.toString()}`);
  }, []);

  const switchBranch = useCallback(async (branch: string, source?: string) => {
    return previewFetch("/api/preview/switch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branch, ...(source ? { source } : {}) }),
    });
  }, []);

  const pull = useCallback(async (branch?: string, force?: boolean) => {
    return previewFetch("/api/preview/pull", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branch, force }),
    });
  }, []);

  const push = useCallback(async (branch: string) => {
    return previewFetch(`/api/preview/push/${branch}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  }, []);

  const fetchRemote = useCallback(async (branch?: string) => {
    return previewFetch("/api/preview/fetch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branch }),
    });
  }, []);

  const getStatus = useCallback(async (): Promise<PreviewStatusResult> => {
    return previewFetch("/api/preview/status");
  }, []);

  const restore = useCallback(async (source: string, paths: string[], commitHash?: string) => {
    return previewFetch("/api/preview/restore", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source, paths, commitHash }),
    });
  }, []);

  const compare = useCallback(
    async (
      source: { branch: string; origin: "local" | "remote" },
      target: { branch: string; origin: "local" | "remote" }
    ): Promise<CompareResult> => {
      return previewFetch("/api/preview/compare", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source, target }),
      });
    },
    []
  );

  // rebase triggers a session-based rebase via the same endpoint used by the
  // Studio's RebaseSessionDialog. Inline conflict resolution is not supported
  // here — if the session returns status="conflict" the caller should open
  // the RebaseSessionDialog on the returned session ID.
  const rebase = useCallback(
    async (branchName: string, _targetBranch?: string, _targetOrigin?: string) => {
      return previewFetch(`/api/preview/rebase-onto-remote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ branch: branchName }),
      });
    },
    []
  );

  const browseRemoteBranches = useCallback(async (): Promise<RemoteBranch[]> => {
    return previewFetch("/api/preview/remote-branches");
  }, []);

  const objectStoreWrite = useCallback(async (
    branch: string,
    slugPath: string,
    updates: { title?: string; body?: string; metadata?: Record<string, unknown> }
  ): Promise<ObjectStoreWriteResult> => {
    return previewFetch("/api/preview/object-store-write", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branch, slugPath, ...updates }),
    });
  }, []);

  const objectStoreCommit = useCallback(async (
    branch: string,
    message: string,
    expectedHead: string
  ): Promise<ObjectStoreCommitResult> => {
    return previewFetch("/api/preview/object-store-commit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ branch, message, expectedHead }),
    });
  }, []);

  const autoFetchStatus = useCallback(async (): Promise<AutoFetchStatusResult> => {
    return previewFetch("/api/preview/auto-fetch-status");
  }, []);

  const autoFetchConfig = useCallback(async (enabled: boolean, interval?: number) => {
    return previewFetch("/api/preview/auto-fetch-config", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled, interval }),
    });
  }, []);

  return {
    isAvailable: isLocalPreview,
    commit,
    selectiveCommit,
    getLog,
    switchBranch,
    pull,
    push,
    fetchRemote,
    getStatus,
    restore,
    compare,
    rebase,
    browseRemoteBranches,
    objectStoreWrite,
    objectStoreCommit,
    autoFetchStatus,
    autoFetchConfig,
  };
}
